/**
 * MagicBI Design System - Layout Components
 * 
 * Comprehensive layout components including Container, Grid, Flex,
 * Stack, and Spacer for consistent layout composition.
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { responsiveMixin } from '../../../tokens/breakpoints';
import type { Breakpoint, ResponsiveValue } from '../../../tokens/breakpoints';

// Container component props
export interface ContainerProps {
  children: React.ReactNode;
  /** Maximum width for container */
  maxWidth?: keyof typeof containerMaxWidths | 'none';
  /** Container padding */
  padding?: keyof typeof paddingSizes;
  /** Center the container */
  centered?: boolean;
  /** Fluid container (full width) */
  fluid?: boolean;
  /** Custom class name */
  className?: string;
}

// Grid component props
export interface GridProps {
  children: React.ReactNode;
  /** Number of columns */
  columns?: ResponsiveValue<number>;
  /** Gap between grid items */
  gap?: keyof typeof gapSizes;
  /** Responsive gap */
  gapX?: keyof typeof gapSizes;
  gapY?: keyof typeof gapSizes;
  /** Auto-fit columns */
  autoFit?: boolean;
  /** Minimum column width for auto-fit */
  minColumnWidth?: string;
  /** Grid template areas */
  areas?: string[];
  /** Align items */
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  /** Justify items */
  justifyItems?: 'start' | 'center' | 'end' | 'stretch';
  /** Custom class name */
  className?: string;
}

// Flex component props
export interface FlexProps {
  children: React.ReactNode;
  /** Flex direction */
  direction?: ResponsiveValue<'row' | 'column' | 'row-reverse' | 'column-reverse'>;
  /** Wrap behavior */
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  /** Gap between flex items */
  gap?: keyof typeof gapSizes;
  /** Align items */
  alignItems?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  /** Justify content */
  justifyContent?: 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly';
  /** Align content */
  alignContent?: 'start' | 'center' | 'end' | 'stretch' | 'space-between' | 'space-around';
  /** Inline flex */
  inline?: boolean;
  /** Custom class name */
  className?: string;
}

// Stack component props (simplified flex column)
export interface StackProps {
  children: React.ReactNode;
  /** Gap between stack items */
  gap?: keyof typeof gapSizes;
  /** Align items */
  align?: 'start' | 'center' | 'end' | 'stretch';
  /** Horizontal stack */
  horizontal?: boolean;
  /** Divide with separator */
  divider?: React.ReactNode;
  /** Custom class name */
  className?: string;
}

// Box component props (general purpose container)
export interface BoxProps {
  children?: React.ReactNode;
  /** HTML element to render */
  as?: keyof JSX.IntrinsicElements;
  /** Padding */
  padding?: keyof typeof paddingSizes;
  /** Margin */
  margin?: keyof typeof marginSizes;
  /** Background color */
  backgroundColor?: string;
  /** Border radius */
  borderRadius?: keyof typeof borderRadiusSizes;
  /** Shadow */
  shadow?: keyof typeof shadowSizes;
  /** Width */
  width?: string;
  /** Height */
  height?: string;
  /** Custom class name */
  className?: string;
}

// Spacer component props
export interface SpacerProps {
  /** Size of the spacer */
  size?: keyof typeof spacerSizes;
  /** Horizontal spacer */
  horizontal?: boolean;
  /** Custom class name */
  className?: string;
}

// Grid item component props
export interface GridItemProps {
  children: React.ReactNode;
  /** Grid column span or specific placement */
  colSpan?: number;
  /** Grid row span or specific placement */
  rowSpan?: number;
  /** Grid column start */
  colStart?: number;
  /** Grid column end */
  colEnd?: number;
  /** Grid row start */
  rowStart?: number;
  /** Grid row end */
  rowEnd?: number;
  /** Grid area name */
  area?: string;
  /** Custom class name */
  className?: string;
}

// Sizing maps
const containerMaxWidths = {
  sm: '540px',
  md: '720px',
  lg: '960px',
  xl: '1140px',
  xxl: '1320px',
} as const;

const paddingSizes = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
} as const;

const marginSizes = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  auto: 'auto',
} as const;

const gapSizes = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
} as const;

const borderRadiusSizes = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
} as const;

const shadowSizes = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
} as const;

const spacerSizes = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
} as const;

// Container component
const StyledContainer = styled.div<ContainerProps>`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  
  ${({ maxWidth }) => maxWidth && maxWidth !== 'none' && css`
    max-width: ${containerMaxWidths[maxWidth]};
  `}
  
  ${({ padding = 'md' }) => css`
    padding-left: ${paddingSizes[padding]};
    padding-right: ${paddingSizes[padding]};
  `}
  
  ${({ centered }) => centered && css`
    text-align: center;
  `}
  
  ${({ fluid }) => fluid && css`
    max-width: none;
    width: 100%;
  `}
  
  /* Responsive padding */
  ${responsiveMixin.up('sm')} {
    ${({ padding = 'md' }) => padding !== 'none' && css`
      padding-left: ${paddingSizes.lg};
      padding-right: ${paddingSizes.lg};
    `}
  }
  
  ${responsiveMixin.up('lg')} {
    ${({ padding = 'md' }) => padding !== 'none' && css`
      padding-left: ${paddingSizes.xl};
      padding-right: ${paddingSizes.xl};
    `}
  }
`;

// Grid component
const StyledGrid = styled.div<GridProps>`
  display: grid;
  
  ${({ columns = 12 }) => {
    if (typeof columns === 'object') {
      let styles = '';
      Object.entries(columns).forEach(([breakpoint, cols]) => {
        const rule = `grid-template-columns: repeat(${cols}, 1fr);`;
        if (breakpoint === 'xs') {
          styles += rule;
        } else {
          styles += `
            ${responsiveMixin.up(breakpoint as Breakpoint)} {
              ${rule}
            }
          `;
        }
      });
      return css`${styles}`;
    }
    return css`grid-template-columns: repeat(${columns}, 1fr);`;
  }}
  
  ${({ gap = 'md' }) => css`
    gap: ${gapSizes[gap]};
  `}
  
  ${({ gapX }) => gapX && css`
    column-gap: ${gapSizes[gapX]};
  `}
  
  ${({ gapY }) => gapY && css`
    row-gap: ${gapSizes[gapY]};
  `}
  
  ${({ autoFit, minColumnWidth = '250px' }) => autoFit && css`
    grid-template-columns: repeat(auto-fit, minmax(${minColumnWidth}, 1fr));
  `}
  
  ${({ areas }) => areas && css`
    grid-template-areas: ${areas.map(area => `"${area}"`).join(' ')};
  `}
  
  ${({ alignItems }) => alignItems && css`
    align-items: ${alignItems === 'start' ? 'flex-start' : alignItems === 'end' ? 'flex-end' : alignItems};
  `}
  
  ${({ justifyItems }) => justifyItems && css`
    justify-items: ${justifyItems === 'start' ? 'flex-start' : justifyItems === 'end' ? 'flex-end' : justifyItems};
  `}
`;

// Flex component
const StyledFlex = styled.div<FlexProps>`
  ${({ inline }) => inline ? 'display: inline-flex;' : 'display: flex;'}
  
  ${({ direction = 'row' }) => {
    if (typeof direction === 'object') {
      let styles = '';
      Object.entries(direction).forEach(([breakpoint, dir]) => {
        const rule = `flex-direction: ${dir};`;
        if (breakpoint === 'xs') {
          styles += rule;
        } else {
          styles += `
            ${responsiveMixin.up(breakpoint as Breakpoint)} {
              ${rule}
            }
          `;
        }
      });
      return css`${styles}`;
    }
    return css`flex-direction: ${direction};`;
  }}
  
  ${({ wrap }) => wrap && css`
    flex-wrap: ${wrap};
  `}
  
  ${({ gap = 'md' }) => css`
    gap: ${gapSizes[gap]};
  `}
  
  ${({ alignItems }) => alignItems && css`
    align-items: ${alignItems === 'start' ? 'flex-start' : alignItems === 'end' ? 'flex-end' : alignItems};
  `}
  
  ${({ justifyContent }) => justifyContent && css`
    justify-content: ${justifyContent === 'start' ? 'flex-start' : justifyContent === 'end' ? 'flex-end' : justifyContent};
  `}
  
  ${({ alignContent }) => alignContent && css`
    align-content: ${alignContent === 'start' ? 'flex-start' : alignContent === 'end' ? 'flex-end' : alignContent};
  `}
`;

// Stack component
const StyledStack = styled.div<StackProps>`
  display: flex;
  flex-direction: ${({ horizontal }) => horizontal ? 'row' : 'column'};
  
  ${({ gap = 'md' }) => css`
    gap: ${gapSizes[gap]};
  `}
  
  ${({ align }) => align && css`
    align-items: ${align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : align};
  `}
`;

// Stack with divider
const StackWithDivider = styled(StyledStack)<StackProps>`
  gap: 0;
  
  > * + * {
    ${({ horizontal, gap = 'md' }) => horizontal ? css`
      margin-left: ${gapSizes[gap]};
    ` : css`
      margin-top: ${gapSizes[gap]};
    `}
  }
`;

interface DividerProps {
  horizontal?: boolean;
  gap?: keyof typeof gapSizes;
}

const Divider = styled.div<DividerProps>`
  ${({ horizontal, gap = 'md' }) => horizontal ? css`
    width: 1px;
    height: 100%;
    background-color: ${({ theme }) => theme.colors.border.subtle};
    margin: 0 ${gapSizes[gap]};
  ` : css`
    height: 1px;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.border.subtle};
    margin: ${gapSizes[gap]} 0;
  `}
`;

// Box component
const StyledBox = styled.div<BoxProps>`
  ${({ padding }) => padding && css`
    padding: ${paddingSizes[padding]};
  `}
  
  ${({ margin }) => margin && css`
    margin: ${marginSizes[margin]};
  `}
  
  ${({ backgroundColor, theme }) => backgroundColor && css`
    background-color: ${backgroundColor.startsWith('#') ? backgroundColor : (theme.colors as any)[backgroundColor] || backgroundColor};
  `}
  
  ${({ borderRadius }) => borderRadius && css`
    border-radius: ${borderRadiusSizes[borderRadius]};
  `}
  
  ${({ shadow }) => shadow && css`
    box-shadow: ${shadowSizes[shadow]};
  `}
  
  ${({ width }) => width && css`
    width: ${width};
  `}
  
  ${({ height }) => height && css`
    height: ${height};
  `}
`;

// Spacer component
const StyledSpacer = styled.div<SpacerProps>`
  ${({ size = 'md', horizontal }) => horizontal ? css`
    width: ${spacerSizes[size]};
    height: 1px;
    display: inline-block;
  ` : css`
    height: ${spacerSizes[size]};
    width: 1px;
  `}
`;

// Grid item component
const StyledGridItem = styled.div<GridItemProps>`
  ${({ colSpan }) => colSpan && css`
    grid-column: span ${colSpan};
  `}
  
  ${({ rowSpan }) => rowSpan && css`
    grid-row: span ${rowSpan};
  `}
  
  ${({ colStart }) => colStart && css`
    grid-column-start: ${colStart};
  `}
  
  ${({ colEnd }) => colEnd && css`
    grid-column-end: ${colEnd};
  `}
  
  ${({ rowStart }) => rowStart && css`
    grid-row-start: ${rowStart};
  `}
  
  ${({ rowEnd }) => rowEnd && css`
    grid-row-end: ${rowEnd};
  `}
  
  ${({ area }) => area && css`
    grid-area: ${area};
  `}
`;

// Component exports
export const Container: React.FC<ContainerProps> = (props) => (
  <StyledContainer {...props} />
);

export const Grid: React.FC<GridProps> = (props) => (
  <StyledGrid {...props} />
);

export const Flex: React.FC<FlexProps> = (props) => (
  <StyledFlex {...props} />
);

export const Stack: React.FC<StackProps> = ({ divider, children, ...props }) => {
  if (divider) {
    return (
      <StackWithDivider {...props}>
        {React.Children.toArray(children).map((child, index) => (
          <React.Fragment key={index}>
            {index > 0 && <Divider horizontal={props.horizontal} gap={props.gap} />}
            {child}
          </React.Fragment>
        ))}
      </StackWithDivider>
    );
  }
  
  return <StyledStack {...props}>{children}</StyledStack>;
};

export const Box: React.FC<BoxProps> = ({ as = 'div', children, ...props }) => (
  <StyledBox as={as} {...props}>
    {children}
  </StyledBox>
);

export const Spacer: React.FC<SpacerProps> = (props) => (
  <StyledSpacer {...props} />
);

export const GridItem: React.FC<GridItemProps> = (props) => (
  <StyledGridItem {...props} />
);